import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { clearContentCache } from "@/lib/useContent";
import { formatApiError } from "@/context/AuthContext";
import {
    Save, RotateCcw, Plus, Trash2, Edit3, Check, X,
    FileText, Film, BookOpen, MapPin, Inbox, Image as ImageIcon, Upload, Navigation, UserCheck, Radio, CalendarClock, Ban, MessageSquareQuote, Star, Mic, Play,
} from "lucide-react";
import { Field } from "../shared.jsx";

export default function SubscribersManager() {
    const [users, setUsers] = useState([]);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [filter, setFilter] = useState("all");

    const load = () => api.get("/admin/users").then((r) => setUsers(r.data));
    useEffect(() => { load(); }, []);

    const grant = async (targetEmail) => {
        setBusy(true);
        try {
            await api.post("/admin/users/grant_subscription", { email: targetEmail, days: 30 });
            toast.success(`${targetEmail} is now a subscriber for 30 days.`);
            setEmail("");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (targetEmail) => {
        if (!window.confirm(`Remove subscription from ${targetEmail}?`)) return;
        setBusy(true);
        try {
            await api.post("/admin/users/revoke_subscription", { email: targetEmail, days: 0 });
            toast.success("Subscription revoked.");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const shown = users.filter((u) => {
        if (filter === "subscribers") return u.is_subscribed;
        if (filter === "free") return !u.is_subscribed;
        return true;
    });

    return (
        <div className="space-y-6" data-testid="subscribers-manager">
            <div className="card p-6 lg:p-8">
                <h2 className="font-serif text-2xl tracking-tight mb-2">Grant subscriber access</h2>
                <p className="text-[var(--sb-text-dim)] text-sm mb-6">
                    When someone pays you £1.89 via PayPal (or any manual method), grant them 30 days of subscriber access here. They must have already registered an account on your site.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                    <div className="flex-1">
                        <label className="overline block mb-2">Their account email</label>
                        <input
                            type="email"
                            className="input-line"
                            placeholder="parent@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="grant-email"
                        />
                    </div>
                    <button
                        onClick={() => email && grant(email)}
                        disabled={busy || !email}
                        className="btn-primary text-sm"
                        style={{padding: "0.75rem 1.5rem"}}
                        data-testid="grant-submit"
                    >
                        <Check size={14} /> Grant 30 days
                    </button>
                </div>
            </div>

            <div className="card p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                    <h2 className="font-serif text-2xl tracking-tight">All users ({users.length})</h2>
                    <div className="flex gap-2">
                        {["all", "subscribers", "free"].map((f) => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                                    filter === f
                                        ? "bg-[var(--sb-brand)] text-[var(--sb-bg)] border-[var(--sb-brand)]"
                                        : "border-[var(--sb-border)] text-[var(--sb-text-dim)] hover:border-[var(--sb-brand)]"
                                }`}
                                data-testid={`subscribers-filter-${f}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="space-y-2">
                    {shown.map((u) => (
                        <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4 border border-[var(--sb-border)]" data-testid={`user-row-${u.email}`}>
                            <div className="min-w-0">
                                <div className="font-serif text-lg truncate">{u.name || "—"}</div>
                                <div className="text-xs text-[var(--sb-text-muted)] font-mono">{u.email}</div>
                                {u.subscription_expires_at && (
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--sb-text-muted)] mt-1">
                                        Expires {new Date(u.subscription_expires_at).toLocaleDateString("en-GB")}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {u.role === "admin" && <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--sb-brand)] border border-[var(--sb-brand)] px-2 py-1">Admin</span>}
                                {u.is_subscribed ? (
                                    <>
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--sb-brand)] border border-[var(--sb-brand)] px-2 py-1">Subscriber</span>
                                        {u.role !== "admin" && (
                                            <button onClick={() => revoke(u.email)} className="text-xs text-[var(--sb-text-muted)] hover:text-red-400 px-2" data-testid={`revoke-${u.email}`}>Revoke</button>
                                        )}
                                    </>
                                ) : (
                                    <button onClick={() => grant(u.email)} disabled={busy} className="btn-outline text-xs" style={{padding: "0.35rem 0.75rem"}} data-testid={`grant-btn-${u.email}`}>
                                        <Check size={12} /> Grant 30 days
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                    {shown.length === 0 && <p className="text-sm text-[var(--sb-text-dim)] text-center py-8">No users in this filter.</p>}
                </div>
            </div>
        </div>
    );
}



/* -------------------------------------------------------------------------- */
/* Voice Logs — moderate visitor audio submissions                            */
